<template>
  <div class="payment-link-page">
    <div class="payment-link-card">
      <h1>Оплата</h1>

      <div v-if="loading" class="payment-link-state">Загружаем данные...</div>
      <div v-else-if="error" class="payment-link-state payment-link-state--error">{{ error }}</div>
      <div v-else>
        <div class="payment-link-info">
          <div class="payment-link-info__item">
            <div class="payment-link-info__label">Назначение оплаты:</div>
            <div class="payment-link-info__value">{{ link.purpose }}</div>
          </div>
          <div class="payment-link-info__item">
            <div class="payment-link-info__label">Сумма:</div>
            <div class="payment-link-info__value payment-link-info__value--amount">{{ formattedAmount }}</div>
          </div>
        </div>

        <div class="payment-link-form">
          <label class="payment-link-field">
            <span class="payment-link-field__label">
              ФИО <span class="payment-link-field__required">*</span>
            </span>
            <input
              v-model="fullName"
              class="payment-link-input"
              type="text"
              placeholder="Иванов Иван Иванович"
              @input="handleNameInput"
            />
          </label>

          <div v-if="formError" class="payment-link-state payment-link-state--error">{{ formError }}</div>

          <button
            class="payment-link-button"
            type="button"
            :disabled="paying"
            @click="pay"
          >
            {{ paying ? 'Перенаправляем…' : 'Оплатить' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaymentLinkPage",
  data() {
    return {
      loading: true,
      paying: false,
      error: "",
      formError: "",
      link: {
        purpose: "",
        amount: 0,
      },
      fullName: "",
    };
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    formattedAmount() {
      const rub = Number(this.link.amount) || 0;
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        maximumFractionDigits: 0,
      }).format(rub);
    },
  },
  created() {
    this.fetchLink();
  },
  methods: {
    async fetchLink() {
      this.loading = true;
      this.error = "";

      try {
        const response = await fetch(`${process.env.VUE_APP_URL_DEV}/api/payment-links/${this.token}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (!response.ok) {
          const message = data?.message || data?.data?.message || "Ссылка недоступна.";
          throw new Error(message);
        }

        this.link = data?.data || { purpose: "", amount: 0 };
      } catch (error) {
        console.error("Error while loading payment link:", error);
        this.error = error?.message || "Не удалось загрузить ссылку.";
      } finally {
        this.loading = false;
      }
    },
    async pay() {
      if (this.paying) {
        return;
      }

      const trimmedName = this.fullName.trim();
      if (!trimmedName) {
        this.formError = "Укажите ФИО, чтобы перейти к оплате.";
        return;
      }

      this.formError = "";
      this.paying = true;

      try {
        const returnUrl = window.location.origin + "/login";

        const response = await fetch(`${process.env.VUE_APP_URL_DEV}/api/payment-links/${this.token}/create-order`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            full_name: trimmedName,
            return_url: returnUrl,
          }),
        });

        const data = await response.json();
        if (!response.ok) {
          const message = data?.message || data?.data?.message || "Не удалось создать оплату.";
          throw new Error(message);
        }

        const paymentUrl = data?.data?.payment_url;
        if (!paymentUrl) {
          throw new Error("Не удалось получить ссылку на оплату.");
        }

        window.location.href = paymentUrl;
      } catch (error) {
        console.error("Error while creating payment:", error);
        this.formError = error?.message || "Не удалось создать оплату. Попробуйте позже.";
      } finally {
        this.paying = false;
      }
    },
    handleNameInput() {
      if (this.formError) {
        this.formError = "";
      }
    },
  },
};
</script>

<style scoped>
.payment-link-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  background: #f4f5f7;
}

.payment-link-card {
  width: min(560px, 100%);
  background: #fff;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.payment-link-card h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 500;
  color: #23282c;
}

.payment-link-info {
  display: grid;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid rgba(87, 90, 96, 0.2);
}

.payment-link-info__item {
  display: grid;
  gap: 6px;
}

.payment-link-info__label {
  font-size: 12px;
  color: rgba(127, 130, 135, 1);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.payment-link-info__value {
  font-size: 18px;
  font-weight: 400;
  color: #23282c;
}

.payment-link-info__value--amount {
  font-size: 20px;
  font-weight: 500;
}

.payment-link-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 8px;
}

.payment-link-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  color: #23282c;
}

.payment-link-field__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.payment-link-field__required {
  color: #d94b4b;
}

.payment-link-input {
  height: 56px;
  border-radius: 4px;
  border: 1px solid rgba(87, 90, 96, 0.2);
  padding: 0 16px;
  font-size: 16px;
  background: rgba(240, 239, 239, 1);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.payment-link-input::placeholder {
  color: rgba(87, 90, 96, 0.6);
}

.payment-link-input:focus {
  outline: none;
  border-color: rgba(35, 40, 44, 1);
  box-shadow: 0 0 0 2px rgba(35, 40, 44, 0.08);
}

.payment-link-button {
  height: 56px;
  width: 100%;
  padding: 0 24px;
  border-radius: 4px;
  border: none;
  background: rgba(35, 40, 44, 1);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.payment-link-button:hover:not(:disabled) {
  background: rgba(35, 40, 44, 0.85);
}

.payment-link-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.payment-link-state {
  font-size: 14px;
  color: #23282c;
}

.payment-link-state--error {
  color: #d94b4b;
}

@media screen and (max-width: 744px) {
  .payment-link-card {
    padding: 24px;
  }

  .payment-link-card h1 {
    font-size: 26px;
  }
}
</style>
